import {
  DesignOptions, DesignSvgs, EnabledDesigns, Designs, PotColors, designOptionsConfig,
  DesignAspectRatios,
} from './constants';

const whitePotImageSrc = 'https://cdn.shopify.com/s/files/1/0068/4215/5090/files/6in_Ceramic_White_2.png?v=1712303189';
const matteWhitePotImageSrc = 'https://cdn.shopify.com/s/files/1/0068/4215/5090/files/6in_Ceramic_Matte_White.png?v=1712903557';
const pinkPotImageSrc = 'https://cdn.shopify.com/s/files/1/0068/4215/5090/files/6in_Ceramic_Fuchsia_2.png?v=1712303189';

// const logoVisualizerOptionsHeight = window.innerHeight > window.innerWidth ? 250 : 444;
// const gutter = 100;
const potImageAspectRatio = 1.065;
const CanvasHeight = 938;
// const CanvasCssHeight = window.innerHeight - logoVisualizerOptionsHeight - gutter;
const CanvasWidth = CanvasHeight * potImageAspectRatio;

const rustColor = '#B9A78C';
const whitePotScaleFactor = 0.7;

const getEngravingDimensions = (potColor: string, engravingDesign: string) => {
  let engravedDesignWidth = 300;
  const designAspectRatio = DesignAspectRatios[engravingDesign] || 1.33;

  if (potColor === PotColors.MATTE_WHITE) {
    engravedDesignWidth *= 1.2;
  }

  const engravedDesignHeight = engravedDesignWidth / designAspectRatio;

  return {
    width: engravedDesignWidth,
    height: engravedDesignHeight,
  };
};

const getEngravedDesignY = (potColor: string, engravingDesign: string) => {
  let positionFactor = 0.7;

  if (potColor === PotColors.FUCHSIA) {
    positionFactor = 0.65;
  } else if (potColor === PotColors.MATTE_WHITE) {
    positionFactor = 0.55;
  }

  const { height: engravedDesignHeight } = getEngravingDimensions(potColor, engravingDesign);
  return positionFactor * CanvasHeight - (engravedDesignHeight / 2);
};

const potImageMap = {} as Record<string, HTMLImageElement>;

const getEngravingColor = (potColor: string) => {
  if (potColor === PotColors.GLOSSY_WHITE) {
    return '#9A9084';
  }

  if (potColor === PotColors.FUCHSIA) {
    return '#9c7108';
  }
  return rustColor;
};

const getRustLogo = (svgElement: SVGSVGElement, potColor: string) => {
  const clonedSvgElement = svgElement.cloneNode(true) as HTMLElement;
  const allSvgElements = clonedSvgElement.querySelectorAll('*');
  const logoColor = getEngravingColor(potColor);

  allSvgElements.forEach((svgChildEl: HTMLElement) => {
    if (svgChildEl.style) {
      // eslint-disable-next-line no-param-reassign
      svgChildEl.style.fill = logoColor;
      // eslint-disable-next-line no-param-reassign
      svgChildEl.style.stroke = logoColor;
    }
  });

  return clonedSvgElement;
};

type EngravingInput = {
  potColor: string,
  engravingDesign: string,
  customText?: string,
  customText2?: string,
  customDate?: string,
};

const svgLocalCache: Record<string, string> = {};

const fetchSvg = async (svgDesign: string) => {
  if (svgLocalCache[svgDesign]) {
    return svgLocalCache[svgDesign];
  }

  const svgPath = DesignSvgs[svgDesign];

  if (!svgPath) {
    return null;
  }

  const svgDesignContent = await (await fetch(svgPath)).text();

  svgLocalCache[svgDesign] = svgDesignContent;

  return svgLocalCache[svgDesign];
};

const fetchAllSvgs = async () => Promise.all(EnabledDesigns.map(fetchSvg));

const drawTextOnCanvas = ({
  potColor,
  engravingDesign,
  customText,
  customText2,
  customDate,
}: EngravingInput) => {
  const canvasEl = document.getElementById('js-engraving-visualizer-canvas') as HTMLCanvasElement;
  const ctx = canvasEl.getContext('2d');
  const fontScaleFactor = potColor === PotColors.GLOSSY_WHITE ? whitePotScaleFactor : 1;
  const fontSizeCustomText = 40 * fontScaleFactor;
  const fontSizeCustomDate = 35 * fontScaleFactor;
  const engravedDesignY = getEngravedDesignY(potColor, engravingDesign);
  const designOption = designOptionsConfig[engravingDesign];
  const {
    height: engravedDesignHeight,
    width: engravedDesignWidth,
  } = getEngravingDimensions(potColor, engravingDesign);
  const engravedDesignX = (CanvasWidth - engravedDesignWidth) / 2;

  if (engravingDesign === Designs.CUSTOM_TEXT) {
    if (customText) {
      ctx.fillStyle = getEngravingColor(potColor);
      ctx.font = `${potColor === PotColors.MATTE_WHITE ? 55 : 45}px Arial`;
      const { width: textWidth } = ctx.measureText(customText);
      const textX = (CanvasWidth - textWidth) / 2;
      const textY = engravedDesignY + (potColor === PotColors.MATTE_WHITE ? 70 : 60); // buffer
      ctx.fillText(customText, textX, textY);
    }

    if (customText2) {
      ctx.fillStyle = getEngravingColor(potColor);
      ctx.font = `${potColor === PotColors.MATTE_WHITE ? 40 : 30}px Arial`;
      const { width: textWidth } = ctx.measureText(customText2);
      const textX = (CanvasWidth - textWidth) / 2;
      const textY = engravedDesignY + (potColor === PotColors.MATTE_WHITE ? 120 : 100); // buffer
      ctx.fillText(customText2, textX, textY);
    }

    if (customDate) {
      ctx.fillStyle = getEngravingColor(potColor);
      ctx.font = `${potColor === PotColors.MATTE_WHITE ? 40 : 30}px Arial`;
      const { width: textWidth } = ctx.measureText(customDate);
      const textX = (CanvasWidth - textWidth) / 2;

      const textY = engravedDesignY + (potColor === PotColors.MATTE_WHITE ? 165 : 135);
      ctx.fillText(customDate, textX, textY);
    }
  } else {
    if (customText && designOption !== DesignOptions.DATE_ONLY) {
      ctx.fillStyle = getEngravingColor(potColor);
      ctx.font = `${fontSizeCustomText}px Arial`;
      const { width: textWidth } = ctx.measureText(customText);
      const textX = (CanvasWidth - textWidth) / 2;
      const textY = engravedDesignY + engravedDesignHeight + 35; // buffer
      ctx.fillText(customText, textX, textY);
    }

    if (customDate) {
      ctx.fillStyle = getEngravingColor(potColor);
      ctx.font = `${fontSizeCustomDate}px Arial`;
      const { width: textWidth } = ctx.measureText(customDate);
      let textX = (CanvasWidth - textWidth) / 2;

      if (engravingDesign === Designs.LOVE_YOU) {
        const [mm, dd, yyyy] = customDate.split('.');
        const dateLine1 = `${mm}.${dd}`;
        const dateLine2 = yyyy;
        textX = potColor === PotColors.GLOSSY_WHITE ? engravedDesignX + 5 : engravedDesignX + 10;

        const textY = engravedDesignY + (engravedDesignHeight * 0.75);
        ctx.font = `${fontSizeCustomText * 0.75}px Arial`;
        ctx.fillText(dateLine1, textX, textY);
        ctx.fillText(dateLine2, textX, textY + 25);
      } else {
        if (engravingDesign === Designs.I_CANT_BELIEF) {
          textX = engravedDesignX;
        }
        const textY = engravedDesignY + engravedDesignHeight
        + ((customText && designOption !== DesignOptions.DATE_ONLY)
          ? fontSizeCustomText : 0) + 35; // buffer
        ctx.fillText(customDate, textX, textY);
      }
    }
  }
};

const drawDesignOnCanvas = async ({
  potColor,
  engravingDesign,
  customText,
  customText2,
  customDate,
}: EngravingInput) => {
  const canvasEl = document.getElementById('js-engraving-visualizer-canvas') as HTMLCanvasElement;
  const ctx = canvasEl.getContext('2d');

  if (DesignSvgs[engravingDesign]) {
    const svgDesignContent = await fetchSvg(engravingDesign);
    const svgLogoContainer: HTMLElement = document.querySelector('.engraving-visualizer__svg-design');
    svgLogoContainer.innerHTML = svgDesignContent;
    const svgDesign = svgLogoContainer.querySelector('svg') as SVGSVGElement;
    const rustColoredLogo = getRustLogo(svgDesign, potColor);

    const { outerHTML } = rustColoredLogo;
    const blob = new Blob([outerHTML], { type: 'image/svg+xml;charset=utf-8' });
    const blobUrl = window.URL.createObjectURL(blob);

    const image = new Image();
    image.crossOrigin = 'anonymous';

    await new Promise<void>((resolve) => {
      image.onload = () => {
        const engravedDesignY = getEngravedDesignY(potColor, engravingDesign);
        const {
          width: engravedDesignWidth,
          height: engravedDesignHeight,
        } = getEngravingDimensions(potColor, engravingDesign);
        const engravedDesignX = (CanvasWidth - engravedDesignWidth) / 2;

        ctx.drawImage(
          image, engravedDesignX, engravedDesignY, engravedDesignWidth, engravedDesignHeight,
        );

        resolve();
      };

      image.src = blobUrl;
    });
  }

  drawTextOnCanvas({
    potColor,
    engravingDesign,
    customText,
    customText2,
    customDate,
  });
};

const clearCanvas = () => {
  const canvasEl = document.getElementById('js-engraving-visualizer-canvas') as HTMLCanvasElement;
  const ctx = canvasEl.getContext('2d');

  ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);
};

const drawPot = (potColor: string) => {
  const potImage = potImageMap[potColor];
  const canvasEl = document.getElementById('js-engraving-visualizer-canvas') as HTMLCanvasElement;
  const ctx = canvasEl.getContext('2d');

  if (potImage) {
    ctx.drawImage(potImage, 0, 0, canvasEl.width, canvasEl.height);
  }
};

const refreshDesignAndText = async ({
  potColor,
  engravingDesign,
  customText,
  customText2,
  customDate,
}: EngravingInput) => {
  clearCanvas();
  drawPot(potColor);

  drawDesignOnCanvas({
    potColor,
    engravingDesign,
    customText,
    customText2,
    customDate,
  });
};

const loadAllPotImages = async (images: string[]) => {
  const allPotImages = await Promise.all(
    images.map(((image) => new Promise<HTMLImageElement>((resolve) => {
      const potImage = new Image();
      potImage.crossOrigin = 'anonymous';

      potImage.onload = () => {
        resolve(potImage);
      };

      potImage.src = image;
    }))),
  );

  return allPotImages;
};

const resizeCanvas = () => {
  const canvasEl = document.getElementById('js-engraving-visualizer-canvas') as HTMLCanvasElement;
  canvasEl.width = CanvasWidth;
  canvasEl.height = CanvasHeight;

  canvasEl.style.width = '100%';
};

const initCanvas = async () => {
  const svgDesignContainer = document.createElement('div');
  svgDesignContainer.classList.add('engraving-visualizer__svg-design');
  document.body.appendChild(svgDesignContainer);

  resizeCanvas();
  const [white, fuchsia, matteWhite] = await loadAllPotImages(
    [whitePotImageSrc, pinkPotImageSrc, matteWhitePotImageSrc],
  );

  potImageMap[PotColors.GLOSSY_WHITE] = white;
  potImageMap[PotColors.FUCHSIA] = fuchsia;
  potImageMap[PotColors.MATTE_WHITE] = matteWhite;
};

fetchAllSvgs();

export { initCanvas, refreshDesignAndText };
